import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Buildings, CaretDown, Check, ArrowsClockwise } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * OrgSwitcher — sidebar dropdown for super admins. Lets them "view as" a specific
 * organization without re-logging in. Sends X-Active-Org on every API call.
 *
 * - Hidden entirely for non-super-admins.
 * - Shows active org's name; "All Organizations" when no override is set.
 */
const OrgSwitcher = () => {
  const {
    user,
    isSuperAdmin,
    activeOrgId,
    setActiveOrgId,
    isOrgOverrideActive,
    token,
    canSwitchOrgs,
    userOrgIds,
  } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!canSwitchOrgs) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        if (isSuperAdmin) {
          // Super admin sees ALL orgs
          const res = await axios.get(`${API}/organizations`, { headers });
          if (!cancel) setOrgs(res.data || []);
        } else {
          // Regular multi-org user: only orgs they belong to
          const res = await axios.get(`${API}/users/${user.id}/organizations`, { headers });
          const list = (res.data?.organizations || []).map(o => ({
            id: o.id,
            name: o.name,
            slug: o.slug,
            is_home: o.is_home,
          }));
          if (!cancel) setOrgs(list);
        }
      } catch {
        if (!cancel) setOrgs([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [canSwitchOrgs, isSuperAdmin, token, user?.id]);

  if (!canSwitchOrgs) return null;

  // activeOrgId empty → All Organizations. Otherwise show the org name.
  const activeName = !activeOrgId
    ? "All Organizations"
    : (orgs.find(o => o.id === activeOrgId)?.name || user?.organization_name || "Loading…");

  return (
    <div className="px-3 pb-2" data-testid="org-switcher">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 px-1 pb-1.5">Viewing</p>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
              isOrgOverrideActive
                ? "bg-[#0EA5E9]/10 border-[#0EA5E9]/30 text-[#0EA5E9] hover:bg-[#0EA5E9]/15"
                : "bg-white/60 border-slate-200 text-slate-900 hover:border-slate-200"
            }`}
            data-testid="org-switcher-trigger"
          >
            <span className="flex items-center gap-2 min-w-0">
              <Buildings size={16} className="flex-shrink-0" />
              <span className="truncate font-medium">{activeName}</span>
            </span>
            <CaretDown size={14} className="flex-shrink-0 opacity-60" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          side="right"
          className="w-64 bg-white border-slate-200 text-slate-900 max-h-96 overflow-y-auto"
          data-testid="org-switcher-menu"
        >
          <DropdownMenuLabel className="text-slate-500 text-xs uppercase tracking-wider">
            View Scope
          </DropdownMenuLabel>
          {isSuperAdmin && (
            <DropdownMenuItem
              onClick={() => setActiveOrgId("")}
              className="cursor-pointer focus:bg-slate-100 focus:text-slate-900"
              data-testid="org-switcher-all"
            >
              <span className="flex items-center gap-2 flex-1">
                <Buildings size={14} />
                <span>All Organizations</span>
                <span className="text-xs text-slate-500 ml-auto">(super admin)</span>
              </span>
              {!activeOrgId && <Check size={14} className="text-[#006994]" />}
            </DropdownMenuItem>
          )}
          {isSuperAdmin && <DropdownMenuSeparator className="bg-slate-100" />}
          <DropdownMenuLabel className="text-slate-500 text-xs uppercase tracking-wider">
            {isSuperAdmin ? "View as Organization" : "Your Organizations"}
          </DropdownMenuLabel>
          {loading && (
            <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-2">
              <ArrowsClockwise size={12} className="animate-spin" /> Loading…
            </div>
          )}
          {!loading && orgs.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">No organizations found</div>
          )}
          {!loading && orgs.map(o => {
            const isActive = o.id === activeOrgId;
            return (
              <DropdownMenuItem
                key={o.id}
                onClick={() => setActiveOrgId(o.id)}
                className="cursor-pointer focus:bg-slate-100 focus:text-slate-900"
                data-testid={`org-switcher-item-${o.slug}`}
              >
                <span className="flex flex-col gap-0.5 flex-1 min-w-0">
                  <span className="flex items-center gap-2">
                    <span className="truncate">{o.name}</span>
                    {o.id === user?.organization_id && (
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 border border-slate-200 rounded px-1">home</span>
                    )}
                  </span>
                  <span className="text-[10px] text-slate-500">
                    {(o.user_count ?? 0)} users · {(o.job_count ?? 0)} jobs
                  </span>
                </span>
                {isActive && <Check size={14} className="text-[#006994] ml-2" />}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default OrgSwitcher;
